import { useMemo, useState, useEffect } from "react";
import ThemeSwitcher from "../components/ThemeSwitcher";
import { getDashboard } from "../store/slices/auth";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

export const bodyClass =
  "bg-slate-950 text-slate-100 min-h-screen flex flex-col font-sans selection:bg-blue-600 selection:text-white";

const getId = (value) =>
  typeof value === "object" ? value?._id || "" : value || "";

const formatLabel = (value) =>
  String(value || "").replaceAll("_", " ");

const getStatusClass = (active) =>
  active
    ? "bg-emerald-950 text-emerald-400 border-emerald-800"
    : "bg-red-950 text-red-400 border-red-800";

export default function UserMarkup({
  officers = [],
  booths = [],
  totalOfficers = 0,
  statistics = {},
  loading = false,
  createLoading = false,
  updateLoading = false,
  deleteLoading = false,
  error = null,

  search,
  setSearch,
  designationFilter,
  setDesignationFilter,
  statusFilter,
  setStatusFilter,

  viewMode,
  setViewMode,

  showModal,
  editingOfficerId,
  form,
  formError,

  subdivisions = [],
  circles = [],
  stations = [],
  officersList = [],
  designations = [],
  dutyWings = [],

  onAddOfficer,
  onEditOfficer,
  onDeleteOfficer,
  onCloseModal,
  onFormChange,
  onSubdivisionChange,
  onCircleChange,
  onDesignationChange,
  onSubmit,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    user,
    dashboard,
    dashboardLoading,
    dashboardError
  } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(getDashboard());
  }, [dispatch]);

  const handleLogout = () => {
    const confirmed = window.confirm(
      "Are you sure you want to logout?"
    );

    if (!confirmed) return;

    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("admin");

    sessionStorage.clear();

    if (window.adminSocket) {
      window.adminSocket.disconnect();
      window.adminSocket = null;
    }

    window.location.replace("/login");
  };

  const selectedCircles = circles.filter(
    (circle) =>
      String(getId(circle.subDivisionId)) ===
      String(form.subDivisionId)
  );

  const selectedStations = stations.filter(
    (station) =>
      String(getId(station.subDivisionId)) ===
      String(form.subDivisionId) &&
      String(getId(station.circleId)) ===
      String(form.circleId)
  );
  const selectedBooths = booths.filter((booth) => {
    const boothStationId =
      typeof booth.policeStationId === "object"
        ? booth.policeStationId?._id
        : booth.policeStationId;

    return (
      form.stationId &&
      String(boothStationId) === String(form.stationId)
    );
  });
  const superiorOfficers = officersList.filter(
    (officer) =>
      officer._id !== editingOfficerId &&
      officer.active !== false
  );

  const isSaving = createLoading || updateLoading;

  const getSubDivisionName = (officer) =>
    officer.subDivisionId?.name ||
    subdivisions.find(
      (item) =>
        item._id === getId(officer.subDivisionId)
    )?.name ||
    "--";

  const getCircleName = (officer) =>
    officer.circleId?.name ||
    circles.find(
      (item) => item._id === getId(officer.circleId)
    )?.name ||
    "--";

  const getStationName = (officer) =>
    officer.stationId?.name ||
    stations.find(
      (item) => item._id === getId(officer.stationId)
    )?.name ||
    "--";



  return (
    <>
      {/* HEADER */}
      <header className="bg-slate-900 border-b border-slate-800 px-4 py-2 flex items-center justify-between shrink-0 shadow-md h-16 relative z-30">
        {/* 1. OFFICIAL IDENTITY & BRANDING */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-700 via-indigo-600 to-blue-500 flex items-center justify-center text-white shadow-lg shadow-blue-900/50 text-xl font-black shrink-0 border border-blue-400/30">
            🇮🇳
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-sm font-black tracking-tight text-white flex items-center gap-2 uppercase">
                Nellore Police

              </h1>
            </div>

          </div>
        </div>
        {/* 2. ICONS-WISE PORTAL NAVIGATION BAR (Proper Website Style) */}
        <nav className="hidden lg:flex items-center bg-slate-950 p-1 rounded-xl border border-slate-800 text-xs font-semibold shadow-inner">
          <a href="/admin" className="px-3 py-1.5 text-slate-300 hover:text-white hover:bg-slate-900 rounded-lg transition flex items-center gap-1.5">
            <span>
              🗺️
            </span>
            <span>
              Live Map
            </span>
          </a>
          <a href="/emergency-sos" className="px-3 py-1.5 text-slate-300 hover:text-white hover:bg-slate-900 rounded-lg transition flex items-center gap-1.5">
            <span>
              🚨
            </span>
            <span>
              Incident Grid
            </span>
          </a>


        </nav>
        {/* 3. ACTIONS, TOOLS DROPDOWN LIST, THEME & USER PROFILE */}
        <div className="flex items-center gap-2">
          {/* Register Officer Primary Quick Action */}

          {/* Upload Primary Quick Action */}
          <button onClick={(event) => { openUploadModal('MASTER') }} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg shadow flex items-center gap-1.5 transition">
            <span>
              📤
            </span>
            {' '}
            <span className="hidden sm:inline">
              Upload
            </span>
            {' '}Roster
          </button>
          {/* Operations & Tools Dropdown (Proper List-Wise View) */}
          <div className="relative inline-block text-left" id="header-tools-dropdown-container">


          </div>

          <ThemeSwitcher className="mr-0.5" labelClass="hidden 2xl:inline" />
          <div className="h-6 w-px bg-slate-800 mx-0.5"></div>
          {/* Authenticated Commander Profile & Logout */}
          <div className="flex items-center gap-2 pl-1">
            <div id="header-commander-insignia" className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black shadow border border-yellow-400/40 shrink-0" style={{ background: "#172554", color: "#fef08a" }}>
              🦁
            </div>
            <div className="hidden sm:block text-right">
              <div
                id="header-commander-name"
                className="text-xs font-black text-white leading-tight truncate max-w-[130px]"
              >
                {user?.displayName || user?.name || user?.username || "Admin"}
              </div>

              <div
                id="header-commander-scope"
                className="text-[10px] text-amber-300 font-bold leading-tight truncate max-w-[160px]"
              >
                👑 {user?.designation || user?.role || "Administrator"}
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              title="Logout"
              className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-red-900/40 text-slate-300 hover:text-red-300 border border-slate-700 flex items-center justify-center text-sm transition cursor-pointer"
            >
              🚪
            </button>
          </div>
        </div>
      </header>

      {/* TOP KPI METRIC STRIP (CLICK ANY CARD TO OPEN IN SEPARATE GRID VIEW) */}
      <div className="bg-slate-900/90 border-b border-slate-800/80 px-4 py-2 shrink-0 grid grid-cols-10 gap-2 text-xs">

        {/* Total Sub Divisions */}
        <div
          onClick={() => navigate("/sub-divisions")}
          className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-purple-400 transition flex items-center justify-between"
        >
          <div>
            <div className="text-[10px] text-purple-400 uppercase font-bold tracking-wider">
              Sub Divisions
            </div>
            <div className="text-base font-black text-purple-300">
              {dashboardLoading ? "..." : dashboard?.totalSubDivisions ?? 0}
            </div>
          </div>
          <span className="text-xl text-purple-400 opacity-50">🏢</span>
        </div>

        {/* Total Circles */}
        <div
          onClick={() => navigate("/circles")}
          className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-sky-400 transition flex items-center justify-between"
        >
          <div>
            <div className="text-[10px] text-sky-400 uppercase font-bold tracking-wider">
              Total Circles
            </div>
            <div className="text-base font-black text-sky-300">
              {dashboardLoading ? "..." : dashboard?.totalCircles ?? 0}
            </div>
          </div>
          <span className="text-xl text-sky-400 opacity-50">⭕</span>
        </div>

        {/* Total Police Stations */}
        <div
          onClick={() => navigate("/stations")}
          className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-teal-400 transition flex items-center justify-between"
        >
          <div>
            <div className="text-[10px] text-teal-400 uppercase font-bold tracking-wider">
              Police Stations
            </div>
            <div className="text-base font-black text-teal-300">
              {dashboardLoading ? "..." : dashboard?.totalStations ?? 0}
            </div>
          </div>
          <span className="text-xl text-teal-400 opacity-50">🚔</span>
        </div>
        <div onClick={() => navigate("/booths")}
          className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-blue-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">
              Total Booths
            </div>
            <div id="stat-total-booths" className="text-base font-black text-white">
              {dashboardLoading ? "..." : dashboard?.totalPollingStations ?? 0}
            </div>
          </div>
          <span className="text-xl opacity-40">
            🗳️
          </span>
        </div>
        <div onClick={() => navigate("/officers")} className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-indigo-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-indigo-400 uppercase font-bold tracking-wider">
              Total Men
            </div>
            <div id="stat-total-staff" className="text-base font-black text-indigo-300">
              {dashboardLoading ? "..." : dashboard?.totalOfficers ?? 0}
            </div>
          </div>
          <span className="text-xl text-indigo-400 opacity-50">
            👮
          </span>
        </div>
        <div onClick={() => navigate("/booths")} className="cursor-pointer bg-rose-950/40 hover:bg-rose-900/40 rounded-lg p-2 border border-rose-600/40 hover:border-rose-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-rose-400 uppercase font-bold tracking-wider">
              Hyper Sensitive
            </div>
            <div id="stat-hyper-sensitive" className="text-base font-black text-rose-400">
              {dashboardLoading ? "..." : dashboard?.hyperSensitiveBooths ?? 0}
            </div>
          </div>
          <span className="text-xl text-rose-400">
            🔥
          </span>
        </div>
        <div onClick={() => navigate("/booths")} className="cursor-pointer bg-amber-950/30 hover:bg-amber-900/40 rounded-lg p-2 border border-amber-600/40 hover:border-amber-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-amber-400 uppercase font-bold tracking-wider">
              Sensitive
            </div>
            <div id="stat-hyper-sensitive" className="text-base font-black text-rose-400">
              {dashboardLoading ? "..." : dashboard?.hyperSensitiveBooths ?? 0}
            </div>
          </div>
          <span className="text-xl text-amber-400">
            ⚠️
          </span>
        </div>

        <div onClick={() => navigate("/staff/on-site")} title="View Verified On-Site Personnel in Grid View" className="cursor-pointer bg-emerald-950/20 hover:bg-emerald-900/30 rounded-lg p-2 border border-emerald-600/30 hover:border-emerald-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-emerald-400 uppercase font-bold tracking-wider">
              On-Site (≤75m)
            </div>
            <div id="stat-onsite" className="text-base font-black text-emerald-400">
              {dashboardLoading ? "..." : dashboard?.atPollingStation ?? 0}
            </div>
          </div>
          <span className="text-xl text-emerald-500 opacity-60">
            ✓
          </span>
        </div>
        <div id="card-kpi-absent" onClick={() => navigate("/staff/absent")} className="cursor-pointer bg-rose-950/30 hover:bg-rose-900/40 rounded-lg p-2 border border-rose-500/50 hover:border-rose-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-rose-400 uppercase font-bold tracking-wider">
              Not Attended
            </div>
            <div id="stat-non-attended" className="text-base font-black text-rose-400">
              {dashboardLoading ? "..." : dashboard?.absentAttendance ?? 0}
            </div>
          </div>
          <span className="text-xl text-rose-500">
            ✕
          </span>
        </div>
        <div id="card-kpi-sos" onClick={() => navigate("/emergency-sos")} title="Click to View Active Emergency SOS Distress Incidents Directly" className="cursor-pointer bg-red-950/30 hover:bg-red-900/50 rounded-lg p-2 border border-red-500/50 hover:border-red-400 transition flex items-center justify-between animate-sos-strobe">
          <div>
            <div className="text-[10px] text-red-400 uppercase font-bold tracking-wider">
              Emergency SOS
            </div>
            <div id="stat-sos" className="text-base font-black text-red-500">
              {dashboardLoading ? "..." : dashboard?.activeSOS ?? 0}
            </div>
          </div>
          <span className="text-xl text-red-500">
            🚨
          </span>
        </div>
      </div >

      {/* PAGE TITLE */}
      <section className="px-5 py-5  border-slate-800 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-blue-400 font-bold mb-1">
                District Administration / Officer Management
              </div>

              <h2 className="text-xl font-black text-white">
                👮 All Officers
              </h2>

              <p className="text-xs text-slate-400 mt-1">
                Create, update and manage police officers and their hierarchy.
              </p>
            </div>

            <button
              type="button"
              onClick={onAddOfficer}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-700"
            >
              <i className="fa-solid fa-plus" />
              Add Officer
            </button>
          </div>
        </div>
      </section>

      {/* STATISTICS */}
      <section className="px-5 py-4  border-slate-800">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-3">
            <p className="text-[10px] uppercase text-slate-400 font-bold">
              Total Officers
            </p>
            <h3 className="text-xl font-black text-white mt-1">
              {statistics.total || 0}
            </h3>
          </div>

          <div className="bg-emerald-950/30 border border-emerald-700/40 rounded-xl p-3">
            <p className="text-[10px] uppercase text-emerald-400 font-bold">
              Active Officers
            </p>
            <h3 className="text-xl font-black text-emerald-400 mt-1">
              {statistics.active || 0}
            </h3>
          </div>

          <div className="bg-red-950/30 border border-red-700/40 rounded-xl p-3">
            <p className="text-[10px] uppercase text-red-400 font-bold">
              Inactive Officers
            </p>
            <h3 className="text-xl font-black text-red-400 mt-1">
              {statistics.inactive || 0}
            </h3>
          </div>

          <div className="bg-blue-950/30 border border-blue-700/40 rounded-xl p-3">
            <p className="text-[10px] uppercase text-blue-400 font-bold">
              Online Officers
            </p>
            <h3 className="text-xl font-black text-blue-400 mt-1">
              {statistics.online || 0}
            </h3>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="px-5 py-4  border-slate-800 bg-slate-950">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-2.5 text-slate-500">
              🔍
            </span>

            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search officer name, ID, badge, phone..."
              className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-9 pr-3 py-2.5 text-xs text-white outline-none focus:border-blue-500"
            />
          </div>

          <select
            value={designationFilter}
            onChange={(event) =>
              setDesignationFilter(event.target.value)
            }
            className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white outline-none"
          >
            <option value="ALL">All Designations</option>
            {designations.map((designation) => (
              <option key={designation} value={designation}>
                {formatLabel(designation)}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white outline-none"
          >
            <option value="ALL">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>

          <div className="flex items-center bg-slate-900 border border-slate-700 rounded-xl p-1">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={`px-3 py-2 rounded-lg text-xs font-bold ${viewMode === "grid"
                ? "bg-blue-600 text-white"
                : "text-slate-400 hover:text-white"
                }`}
            >
              <i className="fa-solid fa-grip mr-2" />
              Grid
            </button>

            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`px-3 py-2 rounded-lg text-xs font-bold ${viewMode === "list"
                ? "bg-blue-600 text-white"
                : "text-slate-400 hover:text-white"
                }`}
            >
              <i className="fa-solid fa-list mr-2" />
              List
            </button>
          </div>

          <button
            type="button"
            onClick={() => {
              setSearch("");
              setDesignationFilter("ALL");
              setStatusFilter("ALL");
            }}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-xl text-xs font-bold text-slate-300"
          >
            Reset Filters
          </button>
        </div>

        <div className="max-w-7xl mx-auto mt-3 text-[10px] text-slate-500">
          Showing{" "}
          <span className="text-white font-bold">
            {officers.length}
          </span>{" "}
          of {totalOfficers} officers
        </div>
      </section>

      {/* OFFICER CONTENT */}
      <main className="flex-1 p-5 bg-slate-950">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20 text-slate-400 text-sm">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3" />
              Loading officers...
            </div>
          ) : error ? (
            <div className="text-center py-16 bg-red-950/20 border border-red-800/40 rounded-2xl">
              <p className="text-red-400 text-sm font-bold">
                Failed to load officers
              </p>
              <p className="text-xs text-slate-400 mt-2">
                {error}
              </p>
            </div>
          ) : officers.length === 0 ? (
            <div className="text-center py-20 bg-slate-900 border border-slate-800 rounded-2xl">
              <div className="text-4xl mb-3">🔍</div>
              <h3 className="text-sm font-bold text-white">
                No Officers Found
              </h3>
              <p className="text-xs text-slate-500 mt-2">
                Try changing your search or filters.
              </p>
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {officers.map((officer) => (
                <div
                  key={officer._id}
                  className="bg-slate-900 border border-slate-800 rounded-2xl p-4 hover:border-blue-500/60 transition shadow-lg"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-sm font-black text-white">
                        {officer.displayName || "Unnamed Officer"}
                      </h3>
                      <p className="text-[11px] text-blue-400 font-bold mt-1">
                        {officer.officerId || "--"}
                      </p>
                    </div>

                    <span
                      className={`text-[9px] px-2 py-1 rounded-lg border font-black ${getStatusClass(
                        officer.active
                      )}`}
                    >
                      {officer.active ? "ACTIVE" : "INACTIVE"}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="text-[9px] px-2 py-1 rounded-lg border border-indigo-700 bg-indigo-950 text-indigo-300 font-bold">
                      {formatLabel(
                        officer.designationName || officer.designation
                      )}
                    </span>

                    <span className="text-[9px] px-2 py-1 rounded-lg border border-cyan-700 bg-cyan-950 text-cyan-300 font-bold">
                      {formatLabel(officer.dutyWing)}
                    </span>
                  </div>

                  <div className="mt-4 space-y-3 text-xs">
                    <div className="flex justify-between gap-3">
                      <span className="text-slate-500">Username</span>
                      <span className="text-slate-200 text-right">
                        {officer.username || "--"}
                      </span>
                    </div>

                    <div className="flex justify-between gap-3">
                      <span className="text-slate-500">Phone</span>
                      <span className="text-slate-200 text-right">
                        {officer.phone || "--"}
                      </span>
                    </div>

                    <div className="flex justify-between gap-3">
                      <span className="text-slate-500">Badge Number</span>
                      <span className="text-slate-200 text-right">
                        {officer.badgeNo || "--"}
                      </span>
                    </div>

                    <div className="flex justify-between gap-3">
                      <span className="text-slate-500">Sub-Division</span>
                      <span className="text-slate-200 text-right">
                        {getSubDivisionName(officer)}
                      </span>
                    </div>

                    <div className="flex justify-between gap-3">
                      <span className="text-slate-500">Circle</span>
                      <span className="text-slate-200 text-right">
                        {getCircleName(officer)}
                      </span>
                    </div>

                    <div className="flex justify-between gap-3">
                      <span className="text-slate-500">Police Station</span>
                      <span className="text-slate-200 text-right">
                        {getStationName(officer)}
                      </span>
                    </div>

                    <div className="flex justify-between gap-3">
                      <span className="text-slate-500">Vehicle</span>
                      <span className="text-slate-200 text-right">
                        {officer.vehicleNo || "--"}
                      </span>
                    </div>

                    <div className="flex justify-between gap-3">
                      <span className="text-slate-500">Shift</span>
                      <span className="text-slate-200 text-right">
                        {officer.shift || "--"}
                      </span>
                    </div>

                    <div className="flex justify-between gap-3">
                      <span className="text-slate-500">Online Status</span>
                      <span
                        className={
                          officer.isOnline
                            ? "text-emerald-400"
                            : "text-slate-500"
                        }
                      >
                        {officer.isOnline ? "ONLINE" : "OFFLINE"}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-800 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => onEditOfficer(officer)}
                      className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold"
                    >
                      <i className="fa-solid fa-pen-to-square mr-2" />
                      Edit
                    </button>

                    <button
                      type="button"
                      disabled={deleteLoading}
                      onClick={() => onDeleteOfficer(officer)}
                      className="px-4 py-2 rounded-lg bg-red-950 hover:bg-red-900 border border-red-800 text-red-300 text-xs font-bold disabled:opacity-50"
                    >
                      <i className="fa-solid fa-trash mr-2" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* LIST VIEW */
            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-x-auto">
              <table className="w-full min-w-[1100px] text-left">
                <thead className="bg-slate-950 text-[10px] uppercase text-slate-400">
                  <tr>
                    <th className="px-4 py-4">Officer</th>
                    <th className="px-4 py-4">Designation</th>
                    <th className="px-4 py-4">Phone</th>
                    <th className="px-4 py-4">Sub-Division</th>
                    <th className="px-4 py-4">Circle</th>
                    <th className="px-4 py-4">Station</th>
                    <th className="px-4 py-4">Duty Wing</th>
                    <th className="px-4 py-4">Status</th>
                    <th className="px-4 py-4 text-right">Actions</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-800">
                  {officers.map((officer) => (
                    <tr
                      key={officer._id}
                      className="hover:bg-slate-800/50"
                    >
                      <td className="px-4 py-4">
                        <p className="text-xs font-bold text-white">
                          {officer.displayName}
                        </p>
                        <p className="text-[10px] text-blue-400 mt-1">
                          {officer.officerId || officer.username}
                        </p>
                      </td>

                      <td className="px-4 py-4 text-xs text-slate-300">
                        {formatLabel(
                          officer.designationName || officer.designation
                        )}
                      </td>

                      <td className="px-4 py-4 text-xs text-slate-300">
                        {officer.phone || "--"}
                      </td>

                      <td className="px-4 py-4 text-xs text-slate-300">
                        {getSubDivisionName(officer)}
                      </td>

                      <td className="px-4 py-4 text-xs text-slate-300">
                        {getCircleName(officer)}
                      </td>

                      <td className="px-4 py-4 text-xs text-slate-300">
                        {getStationName(officer)}
                      </td>

                      <td className="px-4 py-4 text-xs text-slate-300">
                        {formatLabel(officer.dutyWing)}
                      </td>

                      <td className="px-4 py-4">
                        <span
                          className={`text-[9px] px-2 py-1 rounded-lg border font-bold ${getStatusClass(
                            officer.active
                          )}`}
                        >
                          {officer.active ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>

                      <td className="px-4 py-4 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => onEditOfficer(officer)}
                          className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold mr-2"
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          disabled={deleteLoading}
                          onClick={() => onDeleteOfficer(officer)}
                          className="px-3 py-2 rounded-lg bg-red-950 hover:bg-red-900 border border-red-800 text-red-300 text-[10px] font-bold disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* FOOTER */}
      <footer className="px-5 py-3 border-t border-slate-800 bg-slate-900 text-center">
        <p className="text-[10px] text-slate-500">
          Nellore Police • Officer Administration
        </p>
      </footer>

      {/* ADD / EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm flex items-center justify-center p-3 sm:p-5">
          <div className="w-full max-w-5xl max-h-[95vh] overflow-y-auto bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl">
            {/* MODAL HEADER */}
            <div className="sticky top-0 z-10 bg-slate-900 border-b border-slate-800 px-5 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-base font-black text-white">
                  {editingOfficerId ? "Edit Officer" : "Add Officer"}
                </h3>
                <p className="text-[10px] text-slate-400 mt-1">
                  Enter officer details and assign the appropriate hierarchy.
                </p>
              </div>

              <button
                type="button"
                onClick={onCloseModal}
                disabled={isSaving}
                className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-50"
              >
                ✕
              </button>
            </div>

            <form onSubmit={onSubmit} className="p-5 space-y-6">
              {formError && (
                <div className="bg-red-950/40 border border-red-800 rounded-xl p-3 text-xs text-red-300">
                  {formError}
                </div>
              )}

              {/* BASIC DETAILS */}
              <section>
                <h4 className="text-xs font-black uppercase tracking-wider text-blue-400 mb-3">
                  Officer Details
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-[10px] text-slate-400 mb-1">
                      Username *
                    </label>
                    <input
                      required
                      value={form.username}
                      onChange={(event) =>
                        onFormChange("username", event.target.value)
                      }
                      placeholder="officer.username"
                      className="form-input"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-400 mb-1">
                      Display Name *
                    </label>
                    <input
                      required
                      value={form.displayName}
                      onChange={(event) =>
                        onFormChange("displayName", event.target.value)
                      }
                      placeholder="Officer full name"
                      className="form-input"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-400 mb-1">
                      Phone *
                    </label>
                    <input
                      required
                      type="tel"
                      value={form.phone}
                      onChange={(event) =>
                        onFormChange("phone", event.target.value)
                      }
                      placeholder="Mobile number"
                      className="form-input"
                    />
                  </div>



                  <div>
                    <label className="block text-[10px] text-slate-400 mb-1">
                      Badge Number
                    </label>
                    <input
                      value={form.badgeNo || ""}
                      onChange={(event) =>
                        onFormChange("badgeNo", event.target.value)
                      }
                      placeholder="Badge number"
                      className="form-input"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-400 mb-1">
                      Designation *
                    </label>
                    <select
                      required
                      value={form.designation}
                      onChange={(event) =>
                        onDesignationChange(event.target.value)
                      }
                      className="form-input"
                    >
                      {designations.map((designation) => (
                        <option key={designation} value={designation}>
                          {formatLabel(designation)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-400 mb-1">
                      Designation Name
                    </label>
                    <input
                      value={form.designationName || ""}
                      onChange={(event) =>
                        onFormChange("designationName", event.target.value)
                      }
                      placeholder="Optional designation display name"
                      className="form-input"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-400 mb-1">
                      Duty Wing
                    </label>
                    <select
                      value={form.dutyWing}
                      onChange={(event) =>
                        onFormChange("dutyWing", event.target.value)
                      }
                      className="form-input"
                    >
                      {dutyWings.map((wing) => (
                        <option key={wing} value={wing}>
                          {formatLabel(wing)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-400 mb-1">
                      Account Status
                    </label>
                    <select
                      value={String(form.active)}
                      onChange={(event) =>
                        onFormChange("active", event.target.value === "true")
                      }
                      className="form-input"
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* HIERARCHY */}
              <section>
                <h4 className="text-xs font-black uppercase tracking-wider text-blue-400 mb-1">
                  Officer Hierarchy
                </h4>
                <p className="text-[10px] text-slate-500 mb-3">
                  DSP requires a Sub-Division. Inspector requires a Sub-Division and Circle.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-[10px] text-slate-400 mb-1">
                      Sub-Division {form.designation === "DSP" || form.designation === "INSPECTOR" ? "*" : ""}
                    </label>
                    <select
                      required={
                        form.designation === "DSP" ||
                        form.designation === "INSPECTOR"
                      }
                      value={form.subDivisionId}
                      onChange={(event) =>
                        onSubdivisionChange(event.target.value)
                      }
                      className="form-input"
                    >
                      <option value="">Select Sub-Division</option>
                      {subdivisions.map((item) => (
                        <option key={item._id} value={item._id}>
                          {item.name} ({item.subDivisionId})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-400 mb-1">
                      Circle {form.designation === "INSPECTOR" ? "*" : ""}
                    </label>
                    <select
                      required={form.designation === "INSPECTOR"}
                      disabled={!form.subDivisionId}
                      value={form.circleId}
                      onChange={(event) =>
                        onCircleChange(event.target.value)
                      }
                      className="form-input disabled:opacity-40"
                    >
                      <option value="">Select Circle</option>
                      {selectedCircles.map((item) => (
                        <option key={item._id} value={item._id}>
                          {item.name} ({item.circleId})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-400 mb-1">
                      Police Station
                    </label>
                    <select
                      disabled={!form.circleId}
                      value={form.stationId}
                      onChange={(event) =>
                        onFormChange("stationId", event.target.value)
                      }
                      className="form-input disabled:opacity-40"
                    >
                      <option value="">Select Police Station</option>
                      {selectedStations.map((item) => (
                        <option key={item._id} value={item._id}>
                          {item.name} ({item.stationId})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-400 mb-1">
                      Polling Booth
                    </label>

                    <select
                      value={form.boothId || ""}
                      onChange={(event) => {
                        onFormChange("stationId", event.target.value);
                        onFormChange("boothId", "");
                      }}
                      disabled={!form.stationId}
                      className="form-input"
                    >
                      <option value="">
                        {!form.stationId
                          ? "Select Police Station First"
                          : "Select Polling Booth"}
                      </option>

                      {selectedBooths.map((booth) => (
                        <option key={booth._id} value={booth._id}>
                          {booth.name} ({booth.boothId})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </section>

              {/* MODAL FOOTER */}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={onCloseModal}
                  disabled={isSaving}
                  className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-bold text-slate-300 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-xs font-bold text-white disabled:opacity-50"
                >
                  {isSaving
                    ? "Saving..."
                    : editingOfficerId
                      ? "Update Officer"
                      : "Create Officer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .form-input {
          width: 100%;
          background: rgb(2 6 23);
          border: 1px solid rgb(51 65 85);
          border-radius: 0.75rem;
          padding: 0.625rem 0.75rem;
          font-size: 0.75rem;
          color: white;
          outline: none;
        }

        .form-input:focus {
          border-color: rgb(59 130 246);
        }

        .form-input:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .form-input option {
          background: rgb(15 23 42);
          color: white;
        }
      `}</style>
    </>
  );
}
